import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getCalls, getQueues, updateFollowUp } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { anyModalOpen } from '../crm/modalGuard';
import { FiPhone, FiSearch, FiEye, FiRefreshCw, FiLock } from 'react-icons/fi';
import CallDetailModal from '../components/CallDetailModal';
import FollowUpModal from '../components/FollowUpModal';
import MultiSelectQueues from '../components/MultiSelectQueues';
// --- CRM layer (added Phase 6-B, additive) ---------------------------------
import CrmCallCell from '../crm/CrmCallCell';
import CrmTransferCell from '../crm/CrmTransferCell';
import { crmListOpportunities, crmListClients, crmGo, CLOSED_STAGES } from '../crm/crmApi';
// ---------------------------------------------------------------------------

const DATE_FILTERS = [
  { label: 'اليوم', key: 'today' },
  { label: 'أمس', key: 'yesterday' },
  { label: 'آخر 7 أيام', key: '7days' },
  { label: 'هذا الشهر', key: 'month' },
  { label: 'الكل', key: 'all' },
];

const getDateRange = (key) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (key) {
    case 'today':
      return { from: today.toISOString(), to: new Date(today.getTime() + 86400000).toISOString() };
    case 'yesterday': {
      const y = new Date(today.getTime() - 86400000);
      return { from: y.toISOString(), to: today.toISOString() };
    }
    case '7days':
      return { from: new Date(today.getTime() - 7 * 86400000).toISOString(), to: new Date(today.getTime() + 86400000).toISOString() };
    case 'month':
      return { from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(), to: new Date(today.getTime() + 86400000).toISOString() };
    default:
      return { from: '', to: '' };
  }
};

const CallsPage = () => {
  const { user } = useAuth();
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCall, setSelectedCall] = useState(null);
  const [followUpCall, setFollowUpCall] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [followUpStatus, setFollowUpStatus] = useState('');
  const [queue, setQueue] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [agentExtension, setAgentExtension] = useState('');
  const [agents, setAgents] = useState([]);

  // --- CRM layer (added Phase 6-B) -----------------------------------------
  // One request per page load builds a phone -> active opportunities map, so
  // the CRM column costs a single call instead of one /suggest per row.
  const [crmActiveByPhone, setCrmActiveByPhone] = useState({});
  // Phones that already have a NAMED client profile — drives the smart
  // الملف / حفظ العميل button.
  const [crmSavedPhones, setCrmSavedPhones] = useState(null);
  const loadCrmOpportunities = useCallback(() => {
    crmListOpportunities()
      .then((res) => {
        const map = {};
        res.data.opportunities
          .filter((o) => !CLOSED_STAGES.includes(o.crm_stage))
          .forEach((o) => { (map[o.phone_number] = map[o.phone_number] || []).push(o); });
        setCrmActiveByPhone(map);
      })
      .catch(() => setCrmActiveByPhone({}));
    crmListClients()
      .then((res) => setCrmSavedPhones(new Set(
        res.data.clients.filter((c) => (c.full_name || '').trim()).map((c) => c.phone_number)
      )))
      .catch(() => setCrmSavedPhones(new Set()));
  }, []);
  useEffect(() => { loadCrmOpportunities(); }, [loadCrmOpportunities]);
  // ---------------------------------------------------------------------------

  
  useEffect(() => {
    if (user?.role !== 'admin') return;
    const token = localStorage.getItem('token');
    fetch('/api/auth/users', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setAgents(Array.isArray(d) ? d.filter(u => u.extension) : []))
      .catch(() => {});
  }, [user]);

  const fetchCalls = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 50 };
      if (search) params.search = search;
      if (status) params.status = status;
      if (followUpStatus) params.followUpStatus = followUpStatus;
      if (queue && queue.length > 0) params.queue = queue.join(",");
      if (agentExtension) params.agentExtension = agentExtension;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const res = await getCalls(params);
      setCalls(res.data.calls);
      setTotal(res.data.total);
      setTotalPages(res.data.totalPages);
    } catch (error) {
      console.error('Failed to fetch calls:', error);
    } finally {
      setLoading(false);
    }
  }, [page, search, status, followUpStatus, queue, dateFrom, dateTo, agentExtension]);

  // Item 6: skip the background refresh while any modal is open (this page's
  // detail/follow-up modals, or a nested CRM modal) so it can't unmount an open
  // modal and lose typed data. A user-driven fetch (filter change) still runs.
  const pageModalOpen = useRef(false);
  pageModalOpen.current = !!(selectedCall || followUpCall);
  useEffect(() => {
    fetchCalls();
    const interval = setInterval(() => {
      if (pageModalOpen.current || anyModalOpen()) return;
      fetchCalls();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchCalls]);

  useEffect(() => {
    getQueues().then(res => setQueues(res.data)).catch(() => {});
  }, []);

  const handleDateFilter = (key) => {
    setCustomFrom(""); setCustomTo("");
    setActiveFilter(key);
    const range = getDateRange(key);
    setDateFrom(range.from);
    setDateTo(range.to);
    setPage(1);
  };

  const handleFollowUpSubmit = async (callId, data) => {
    try {
      await updateFollowUp(callId, data);
      setFollowUpCall(null);
      fetchCalls();
    } catch (error) {
      console.error('Failed to update follow-up:', error);
    }
  };

  const handleUnlock = async (call) => {
    try {
      await updateFollowUp(call.id, { followUpStatus: 'pending' });
      fetchCalls();
    } catch (error) {
      console.error('Failed to unlock:', error);
      alert(error.response?.data?.error || 'فشل فك التعليق');
    }
  };

  const handleLock = async (call) => {
    try {
      await updateFollowUp(call.id, { followUpStatus: 'in_progress' });
      fetchCalls();
    } catch (error) {
      console.error('Failed to lock:', error);
      alert(error.response?.data?.error || 'فشل التعليق');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${date} ${time}`;
  };

  const getStatusBadge = (s) => {
    const map = {
      answered: { label: 'مردود عليها', cls: 'badge-answered' },
      missed_in_queue: { label: 'فائتة في الكيو', cls: 'badge-missed' },
      missed_before_queue: { label: 'أغلق أثناء رسالة الترحيب', cls: 'badge-missed' },
      abandoned: { label: 'لم يتم الرد وأغلق العميل', cls: 'badge-abandoned' },
      after_hours: { label: 'خارج الدوام', cls: 'badge-after-hours' },
      voicemail: { label: 'بريد صوتي', cls: 'badge-voicemail' }
    };
    const info = map[s] || { label: s, cls: '' };
    return <span className={`badge ${info.cls}`}>{info.label}</span>;
  };

  const getFollowUpBadge = (s, autoDetected, followUpBy) => {
    const map = {
      not_required: { label: 'لا يلزم', cls: 'badge-not-required' },
      pending: { label: 'بانتظار المتابعة', cls: 'badge-pending' },
      in_progress: { label: 'جاري المتابعة', cls: 'badge-retry' },
      callback_done_answered: { label: 'تم الرد', cls: 'badge-callback-answered' },
      callback_done_no_answer: { label: 'لم يرد', cls: 'badge-callback-no-answer' },
      retry_later: { label: 'إعادة محاولة', cls: 'badge-retry' },
      closed: { label: 'مغلقة', cls: 'badge-closed' }
    };
    const info = map[s] || { label: s, cls: '' };
    return (
      <span>
        <span className={`badge ${info.cls}`}>{info.label}</span>
        {s === 'in_progress' && followUpBy && (
          <span className="crm-by">({followUpBy})</span>
        )}
        {autoDetected && <span className="auto-detected" style={{ marginRight: 4 }}>تلقائي</span>}
      </span>
    );
  };

  const handleCall = (number) => {
    window.open(`https://ososenjaz.3cx.asia/#/people/phonedialog/${number}`, '_blank');
  };

  return (
    // Batch 5: calm/enterprise identity — `crm-calm` opts this page into the
    // token-driven surface layer in crm/crm.css. Styling scope only; no logic,
    // column, filter, ACL or data-flow change anywhere in this file.
    <div className="crm-calm crm-calm-page">
      <div className="page-header">
        <h1 className="page-title">المكالمات الموحّدة</h1>
        {user?.role === "admin" && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="crm-count">الإجمالي: {total}</span>
            <button className="btn btn-success btn-sm" onClick={() => {
              const params = new URLSearchParams();
              if (dateFrom) params.set('dateFrom', dateFrom);
              if (dateTo) params.set('dateTo', dateTo);
              if (queue && queue.length > 0) params.set("queue", queue.join(","));
              if (status) params.set('status', status);
              if (followUpStatus) params.set('followUpStatus', followUpStatus);
              if (search) params.set('search', search);
              const token = localStorage.getItem('token');
              window.open(`/api/calls/export/csv?${params.toString()}&token=${token}`, '_blank');
            }}>
              تصدير Excel
            </button>
            <button className="btn btn-outline btn-sm" onClick={async () => {
              try { await fetch('/api/sync', { method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }); } catch(e) {}
              fetchCalls();
            }}>
              <FiRefreshCw size={14} /> تحديث
            </button>
          </div>
        )}
      </div>

      <div className="crm-filterbar">
        {[
          { key: 'today', label: 'اليوم' },
          { key: 'yesterday', label: 'أمس' },
          { key: '7days', label: 'آخر 7 أيام' },
          { key: 'month', label: 'هذا الشهر' },
          { key: 'all', label: 'الكل' }
        ].map(f => (
          <button
            key={f.key}
            className={`btn btn-sm ${activeFilter === f.key ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => handleDateFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
        <span className="lbl" style={{ marginRight: 8 }}>أو فترة مخصصة:</span>
        <span className="lbl">من</span>
        <input type="date" value={customFrom} onChange={e => {
          setCustomFrom(e.target.value); setActiveFilter("");
          setDateFrom(e.target.value ? new Date(e.target.value).toISOString() : "");
          setPage(1);
        }} className="filter-input" style={{ width: 150 }} />
        <span className="lbl">إلى</span>
        <input type="date" value={customTo} onChange={e => {
          setCustomTo(e.target.value); setActiveFilter("");
          if (e.target.value) { const dd = new Date(e.target.value); dd.setHours(23,59,59,999); setDateTo(dd.toISOString()); }
          else setDateTo("");
          setPage(1);
        }} className="filter-input" style={{ width: 150 }} />
        {(customFrom || customTo) && (
          <button className="btn btn-outline btn-sm" onClick={() => { setCustomFrom(""); setCustomTo(""); handleDateFilter("all"); }}>مسح</button>
        )}
      </div>

      <div className="table-container">
        <div className="table-header">
          <div className="table-filters">
            <div style={{ position: 'relative' }}>
              <FiSearch style={{ position: 'absolute', right: 10, top: 10, color: '#999' }} />
              <input
                className="filter-input"
                placeholder="بحث بالرقم أو الاسم..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                style={{ paddingRight: 32 }}
              />
            </div>
            <select className="filter-select" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
              <option value="">كل الحالات</option>
              <option value="answered">مردود عليها</option>
              <option value="missed_in_queue">فائتة في الكيو</option>
              <option value="missed_before_queue">أغلق أثناء رسالة الترحيب</option>
              <option value="abandoned">لم يتم الرد وأغلق العميل</option>
              <option value="after_hours">خارج الدوام</option>
              <option value="voicemail">بريد صوتي</option>
            </select>
            <select className="filter-select" value={followUpStatus} onChange={(e) => { setFollowUpStatus(e.target.value); setPage(1); }}>
              <option value="">كل حالات المتابعة</option>
              <option value="pending">بانتظار المتابعة</option>
              <option value="in_progress">جاري المتابعة</option>
              <option value="callback_done_answered">تم الرد</option>
              <option value="callback_done_no_answer">لم يرد</option>
              <option value="retry_later">إعادة محاولة</option>
              <option value="closed">مغلقة</option>
              <option value="not_required">لا يلزم</option>
            </select>
            <MultiSelectQueues queues={queues} selected={queue} onChange={(v) => { setQueue(v); setPage(1); }} />
            {user?.role === 'admin' && (
              <select className="filter-select" value={agentExtension} onChange={(e) => { setAgentExtension(e.target.value); setPage(1); }}>
                <option value="">كل الموظفين</option>
                {agents.map(a => <option key={a.extension} value={a.extension}>{a.fullName} ({a.extension})</option>)}
              </select>
            )}
          </div>
        </div>

        {loading ? (
          <div className="crm-empty">جاري التحميل...</div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>الرقم</th>
                    <th>الاسم</th>
                    <th>الكيو / المصدر</th>
                    <th>المحاولات</th>
                    <th>آخر اتصال</th>
                    <th>الحالة</th>
                    <th>رد بواسطة</th>
                    <th>المتابعة</th>
                    <th>متابعة بواسطة</th>
                    <th>وقت المتابعة</th>
                    {/* The standalone "عميل" (محفوظ / حفظ) column was removed:
                        it is now one smart button inside the الفرص cell —
                        "الملف" when a client profile exists, "حفظ العميل" when
                        it does not. */}
                    {/* CRM columns — split into two in Phase 6-C */}
                    <th>الفرص</th>
                    <th>تحويل المتابعة</th>
                    <th>إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {calls.length === 0 ? (
                    <tr><td colSpan="13" className="crm-empty">لا توجد مكالمات</td></tr>
                  ) : (
                    calls.map(call => (
                      <tr key={call.id} className={call.followUpStatus === 'in_progress' ? 'crm-row-locked' : undefined}>
                        <td style={{ fontWeight: 600, direction: 'ltr', textAlign: 'right' }}>{call.callerNumber}</td>
                        <td>{call.callerName || call.callerNumber}</td>
                        <td><span className="badge badge-queue">{call.queueName || call.callSource}</span></td>
                        <td style={{ textAlign: 'center' }}>{call.totalInboundAttempts}</td>
                        <td style={{ fontSize: 13 }}>{formatDate(call.lastCallAt)}</td>
                        <td>{getStatusBadge(call.lastCallStatus)}</td>
                        <td>{call.answeredBy ? `${call.answeredBy} (${call.answeredByExtension})` : '-'}</td>
                        <td>{getFollowUpBadge(call.followUpStatus, call.autoDetectedCallback, call.followUpBy)}</td>
                        <td>{call.followUpBy || '-'}</td>
                        <td style={{ fontSize: 13 }}>{formatDate(call.followUpAt)}</td>
                        {/* CRM cells (Phase 6-B, split into two columns in 6-C).
                            Both respect the same lock/mask rule as the existing
                            action buttons: a call locked by another agent shows
                            nothing here either. */}
                        <td>
                          {(() => {
                            const lockedByOther = call.followUpStatus === 'in_progress' && call.followUpByExtension !== user?.extension && user?.role !== 'admin';
                            if (lockedByOther) return null;
                            return (
                              <CrmCallCell
                                call={call}
                                activeByPhone={crmActiveByPhone}
                                savedPhones={crmSavedPhones}
                                onOpenClient={() => crmGo('crm-client')}
                                onOpenOpportunity={() => crmGo('crm-opportunity')}
                                onChanged={loadCrmOpportunities}
                              />
                            );
                          })()}
                        </td>
                        <td>
                          {(() => {
                            const lockedByOther = call.followUpStatus === 'in_progress' && call.followUpByExtension !== user?.extension && user?.role !== 'admin';
                            if (lockedByOther) return null;
                            return <CrmTransferCell call={call} onChanged={loadCrmOpportunities} />;
                          })()}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 4 }}>
                            {(() => {
                              const lockedByOther = call.followUpStatus === 'in_progress' && call.followUpByExtension !== user?.extension && user?.role !== 'admin';
                              if (lockedByOther) return null;
                              return (<>
                                <button className="btn btn-call btn-sm" onClick={() => handleCall(call.callerNumber)} title="اتصال عبر 3CX">
                                  <FiPhone size={12} />
                                </button>
                                <button className="btn btn-outline btn-sm" onClick={() => setSelectedCall(call)} title="تفاصيل">
                                  <FiEye size={12} />
                                </button>
                              </>);
                            })()}
                            {(() => {
                              const isLocked = call.followUpStatus === 'in_progress';
                              const isMine = call.followUpByExtension === user?.extension;
                              const canLock = call.followUpStatus === 'pending' || call.followUpStatus === 'retry_later' || call.followUpStatus === 'callback_done_no_answer';
                              const isAdmin = user?.role === 'admin';
                              if (isLocked && (isMine || isAdmin)) {
                                return (<button className="btn btn-warning btn-sm" onClick={() => handleUnlock(call)} title="فك التعليق"><FiLock size={12} /></button>);
                              }
                              if (canLock) {
                                return (<button className="btn btn-warning btn-sm" onClick={() => handleLock(call)} title="جاري المتابعة"><FiLock size={12} /></button>);
                              }
                              return null;
                            })()}
                            {(() => {
                              if (call.followUpStatus === 'not_required') return null;
                              const isLocked2 = call.followUpStatus === 'in_progress';
                              const isMine2 = call.followUpByExtension === user?.extension;
                              const isAdmin2 = user?.role === 'admin';
                              const isCompleted = ['callback_done_answered','callback_done_no_answer','closed'].includes(call.followUpStatus);
                              if (isCompleted && !isAdmin2) return null;
                              if (isLocked2 && !isMine2 && !isAdmin2) return null;
                              return (<button className="btn btn-primary btn-sm" onClick={() => setFollowUpCall(call)} title="تحديث المتابعة">تحديث</button>);
                            })()}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>السابق</button>
                <span style={{ fontSize: 13 }}>صفحة {page} من {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>التالي</button>
              </div>
            )}
          </>
        )}
      </div>

      {selectedCall && (
        <CallDetailModal call={selectedCall} onClose={() => setSelectedCall(null)} onUpdate={fetchCalls} currentUser={user} />
      )}

      {followUpCall && (
        <FollowUpModal
          call={followUpCall}
          onClose={() => setFollowUpCall(null)}
          onSubmit={handleFollowUpSubmit}
        />
      )}
    </div>
  );
};

export default CallsPage;